import moment from 'moment';

import { ONE_SIDED_CONDITION_VALUES, REPORT_CONTROL_TYPE } from './report-designer.constants';
import { isTrue } from '../../utils/commonUtils';
import { getDateFormat } from '../../utils/dateUtils';
import { isExpressionValue } from '../utils/report-designer.utils';
import { isFieldPropUnique } from '../utils/validation.utils';

export const sortConditionValidationProps = [
  //Validate that the 'Field Name' is specified in the 'Sort' row
  {
    prop: 'dataField',
    errorMessage: 'Field Name is required | Sort',
    condition: ({ dataField }) => !dataField,
  },
  //Validate that the 'Field Name' is unique in the 'Sort' table
  {
    prop: 'dataField',
    errorMessage: 'Field Name should be unique | Sort',
    condition: ({ dataField, parentId }, reportModel) => {
      const siblingControls = reportModel[parentId].childrenNodes.map((id) => ({
        dataField: reportModel[id].dataField,
      }));

      return (
        dataField &&
        !isFieldPropUnique({ items: siblingControls, prop: 'dataField', value: dataField })
      );
    },
  },
];

export const chartValidationProps = [
  //Validate that the 'series value' is specified if the 'dynamic series' is set to true
  {
    prop: 'dataField',
    errorMessage: 'The dynamic series is enabled, but no series value is specified | Chart',
    condition: ({ dynamicSeries, dataField }) => isTrue(dynamicSeries) && !dataField,
  },
];

export const chartValueValidationProps = [
  //Validate that the value is specified for all value type other than value type 'other'
  {
    prop: 'value',
    errorMessage: 'No chart value is specified | Chart Value',
    condition: ({ valueType, value }) =>
      ['Count', 'Sum', 'Avg', 'Max', 'Min'].includes(valueType) && !value,
  },
  //Validate that the value is an expression for value type 'other'
  {
    prop: 'value',
    errorMessage: 'No expression set for value type : other | Chart Value',
    condition: ({ valueType, value }) =>
      valueType === 'other' && (!value || !isExpressionValue(value)),
  },
];

export const chartGroupValidationProps = [
  //Validate that the chart 'group by' property is specified
  {
    prop: 'category',
    errorMessage: 'No chart grouping is specified | Chart Group',
    condition: ({ category }) => !category,
  },
];

export const crossTabRowValidationProps = [
  //Validate that the data field is specified for all crosstab row control
  {
    prop: 'dataField',
    errorMessage: 'No data field is specified | Cross Tab Row',
    condition: ({ dataField }) => !dataField,
  },
  //Validate that the display field is specified for all crosstab row control
  {
    prop: 'rowGroupDisplay',
    errorMessage: 'No display field is specified | Cross Tab Row',
    condition: ({ rowGroupDisplay }) => !rowGroupDisplay,
  },
];

export const crossTabColumnValidationProps = [
  //Validate that the data field is specified for all crosstab column control
  {
    prop: 'dataField',
    errorMessage: 'No data field is specified | Cross Tab Column',
    condition: ({ dataField }) => !dataField,
  },
  //Validate that the display field is specified for all crosstab column control
  {
    prop: 'columnGroupDisplay',
    errorMessage: 'No display field is specified | Cross Tab Column',
    condition: ({ columnGroupDisplay }) => !columnGroupDisplay,
  },
];

export const crossTabDataValidationProps = [
  //Validate that the data field is specified for all crosstab data control
  {
    prop: 'dataField',
    errorMessage: 'No data field is specified | Cross Tab Data',
    condition: ({ dataField }) => !dataField,
  },
];

export const groupHeaderDataValidationProps = [
  //Validate that the 'group by' column is specified
  {
    prop: 'groupByColumn',
    errorMessage: 'No group by column specified | Group',
    condition: ({ groupByColumn }) => !groupByColumn,
  },
  //Validate that the 'summary column' is specified for all summary type other than summary type 'count' and 'other'
  {
    prop: 'groupHeaderSummaryColumn',
    errorMessage: 'No group header summary column specified | Group',
    condition: ({ groupHeaderSummaryType, groupHeaderSummaryColumn }) =>
      ['Sum', 'Avg', 'Max', 'Min'].includes(groupHeaderSummaryType) && !groupHeaderSummaryColumn,
  },
  //Validate that the 'summary column' is an expression for summary value type 'other'
  {
    prop: 'groupHeaderSummaryColumn',
    errorMessage: 'No expression in group header summary column is specified | Group',
    condition: ({ groupHeaderSummaryType, groupHeaderSummaryColumn }) =>
      groupHeaderSummaryType === 'other' && !isExpressionValue(groupHeaderSummaryColumn),
  },
  //Validate that the 'summary column' is specified for all summary type other than 'count' and 'other'
  {
    prop: 'groupFooterSummaryColumn',
    errorMessage: 'No group footer summary column specified | Group',
    condition: ({ groupFooterSummaryType, groupFooterSummaryColumn }) =>
      ['Sum', 'Avg', 'Max', 'Min'].includes(groupFooterSummaryType) && !groupFooterSummaryColumn,
  },
  //Validate that the 'summary column' is an expression for summary value type 'other'
  {
    prop: 'groupFooterSummaryColumn',
    errorMessage: 'No expression in group footer summary column is specified | Group',
    condition: ({ groupFooterSummaryType, groupFooterSummaryColumn }) =>
      groupFooterSummaryType === 'other' && !isExpressionValue(groupFooterSummaryColumn),
  },
];

export const dataColumnValidationProps = [
  //Validate if the data field or the image has been specified for the data column
  {
    prop: 'dataField|imageURL',
    errorMessage: 'No field or image specified for the data column | Data Column',
    condition: ({ dataField, imageURL }) => !dataField && !imageURL,
  },
];

export const dataColumnContainerValidationProps = [
  //Validate if there is at least one data column in the report
  {
    prop: 'dataColumnContainer',
    errorMessage: 'No data column(s) specified | Report Data',
    condition: ({ childrenNodes }) => !childrenNodes.length,
    // condition: ({ containerChildren }, reportModel) => {!containerChildren.length},
  },
  //Validate if the data field has been specified for at least one data column in the report
  {
    prop: 'dataColumnContainer',
    errorMessage: 'No data field specified for any of the data column | Report Data',
    condition: ({ childrenNodes }, reportModel) => {
      // const is = reportModel[containerId].childrenNodes.some((id) => reportModel[id].dataField)
      return childrenNodes.length && !childrenNodes.some((id) => reportModel[id].dataField);
    },
  },
];

export const pageHeaderValidationProps = [
  //Validate if report title is left blank
  {
    prop: 'reportTitle',
    errorMessage: 'No report title specified | Page Header',
    condition: ({ reportTitle }) => !reportTitle,
  },
];

export const reportFilterTableValidationProps = [
  //Validate if the filter field has been specified
  {
    prop: 'dataField',
    errorMessage: 'Field Name is required | Report Filter',
    condition: ({ dataField }) => !dataField,
  },
  //Ensure if the value has not been specified at design time, then the user should be allowed to change the value at runtime
  //No filter value specified for a required field. The value should be allowed to change.
  {
    prop: 'value|allowChange',
    errorMessage:
      'No filter value specified. The value should be allowed to change | Report Filter',
    condition: ({ value, allowChange, condition }) =>
      !value && !isTrue(allowChange) && !ONE_SIDED_CONDITION_VALUES.includes(condition),
  },
];

export const dataFilterTableValidationProps = [
  //Validate if the 'Field Name' has been specified
  {
    prop: 'dataField',
    errorMessage: 'Field Name is required | Data Filter',
    condition: ({ dataField }) => !dataField,
  },
  //Validate if the 'Filter Field Name' has been specified
  {
    prop: 'dataFilterField',
    errorMessage: 'Filter Field Name is required | Data Filter',
    condition: ({ dataFilterField, condition }) =>
      !dataFilterField && !ONE_SIDED_CONDITION_VALUES.includes(condition),
  },
];

const distributionValidationProps = [
  //Validate 'Start Date' to be a valid date
  {
    prop: 'startDate',
    errorMessage: 'Please enter a valid date for Start Date | Distribution',
    condition: ({ startDate }) => {
      const dateFormat = getDateFormat(startDate);
      const date = moment(startDate, dateFormat);

      // value could be a string e.g. "2023-01-27T08:27:23.957", "10-may-____"
      const invalidStringDate =
        typeof startDate === 'string' && (startDate.includes('_') || !moment(startDate).isValid());
      // value could be a Date class instance
      const invalidDateInstance = typeof startDate !== 'string' && !date.isValid();

      const valueIsInvalid = startDate && (invalidStringDate || invalidDateInstance);

      return valueIsInvalid;
    },
  },
  {
    prop: 'code',
    errorMessage: 'Code should be unique | Distribution',
    condition: ({ distributionList, code }) =>
      code && !isFieldPropUnique({ items: distributionList, prop: 'code', value: code }),
  },
  {
    prop: 'code',
    errorMessage: 'Code is required | Distribution',
    condition: ({ code, distributionList }) => !code && distributionList,
  },
];

export const MAX_REPORT_NAME_LENGTH = 100;

export const REPORT_CREATION_VALIDATION_ERROR_MESSAGES = {
  MAX_LENGTH: `Report Name cannot be greater than ${MAX_REPORT_NAME_LENGTH} characters`,
  NAME_REQUIRED: 'Report Name is required',
  REPORT_MODEL_REQUIRED: 'Report Model is required',
  SPECIAL_CHARACTERS: 'Report Name cannot contain special characters',
  UNIQUE: 'Report Name is not unique',
};

export const VALIDATION_PROPS_BY_CONTROL_TYPE = {
  [REPORT_CONTROL_TYPE.PAGE_HEADER]: pageHeaderValidationProps,
  [REPORT_CONTROL_TYPE.CHART]: chartValidationProps,
  [REPORT_CONTROL_TYPE.CHART_VALUE]: chartValueValidationProps,
  [REPORT_CONTROL_TYPE.CHART_GROUP]: chartGroupValidationProps,
  [REPORT_CONTROL_TYPE.CROSS_TAB_ROW]: crossTabRowValidationProps,
  [REPORT_CONTROL_TYPE.CROSS_TAB_COLUMN]: crossTabColumnValidationProps,
  [REPORT_CONTROL_TYPE.CROSS_TAB_DATA]: crossTabDataValidationProps,
  [REPORT_CONTROL_TYPE.GROUP_HEADER]: groupHeaderDataValidationProps,
  [REPORT_CONTROL_TYPE.DATA_COLUMN]: dataColumnValidationProps,
  [REPORT_CONTROL_TYPE.DATA_COLUMN_CONTAINER]: dataColumnContainerValidationProps,
  [REPORT_CONTROL_TYPE.FILTER_CONDITION]: reportFilterTableValidationProps,
  [REPORT_CONTROL_TYPE.DATA_FILTER_CONDITION]: dataFilterTableValidationProps,
  [REPORT_CONTROL_TYPE.SORT_CONDITION]: sortConditionValidationProps,
  [REPORT_CONTROL_TYPE.DISTRIBUTION]: distributionValidationProps,
};

export const MAX_VIRTUAL_ENTITY_NAME_LENGTH = 25;

export const REPORT_PUBLISH_VIRTUAL_ENTITY_ERROR_MESSAGES = {
  MAX_LENGTH: `Virtual Entity Name cannot be greater than ${MAX_VIRTUAL_ENTITY_NAME_LENGTH} characters`,
  NAME_REQUIRED: 'Virtual Entity Name is required',
  UNIQUE: 'This virtual entity already exists',
};

export const UNSUPPORTED_SAVED_SEARCH_ELEMENTS_MESSAGE = `The report contains unsupported element(s) like 
   Charts, Groups, Matrices or Expressions, which are not supported by Saved Searches. 
   Do you want to continue anyway?`;
